/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gauge, ShieldCheck, Leaf, Certificate, MagnifyingGlass, FileText,
  Trash as TrashIcon, Drop, TestTube, Syringe, Flask,
  CheckCircle, XCircle, Warning, ArrowRight, DownloadSimple, User,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66";
const GREEN = "#2D5A27";
const RED = "#B12A1E";
const GOLD = "#C9A227";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};

// ============================================================================
// Regulatory Dashboard (upgraded with hierarchy filter, performance, PDF)
// ============================================================================
const LEVELS = [
  { key: "state", label: "State" },
  { key: "district", label: "District" },
  { key: "lsgd", label: "LSGD" },
  { key: "ward", label: "Ward" },
];

export function GovRegulatoryDashboard() {
  const [d, setD] = useState(null);
  const [bmw, setBmw] = useState(null);
  const [sust, setSust] = useState(null);
  const [hier, setHier] = useState({ states: [], districts: [], lsgds: [], wards: [] });
  const [level, setLevel] = useState("district");
  const [scopeId, setScopeId] = useState("");
  const [perf, setPerf] = useState(null);
  const [perfSort, setPerfSort] = useState("compliance_desc");
  const [load, setLoading] = useState(true);

  // Cascading filter state (dashboard filter bar)
  const [fState, setFState] = useState("");
  const [fDistrict, setFDistrict] = useState("");
  const [fLsgd, setFLsgd] = useState("");
  const [fWard, setFWard] = useState("");

  useEffect(() => { (async () => {
    try {
      const [a, b, h, s] = await Promise.all([
        api.get("/gov/dashboard"),
        api.get("/gov/bmw-analytics?period=monthly"),
        api.get("/gov/hierarchy"),
        api.get("/gov/sustainability"),
      ]);
      setD(a.data); setBmw(b.data); setHier(h.data); setSust(s.data);
    } catch (e) { toast.error(fmtErr(e)); }
    setLoading(false);
  })(); }, []);

  // Re-fetch dashboard + performance when scope filter changes
  useEffect(() => {
    (async () => {
      try {
        const q = scopeId ? `?level=${level}&scope_id=${scopeId}` : "";
        const r = await api.get(`/gov/dashboard${q}`);
        setD(r.data);
      } catch (e) { toast.error(fmtErr(e)); }
    })();
  }, [level, scopeId]);

  // Performance table: re-fetch on level + sort change
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/gov/performance?level=${level}&sort=${perfSort}&limit=20`);
        setPerf(r.data);
      } catch (e) { toast.error(fmtErr(e)); }
    })();
  }, [level, perfSort]);

  const filteredDistricts = useMemo(() =>
    fState ? hier.districts.filter(x => x.parent_id === fState) : hier.districts,
    [fState, hier.districts]);
  const filteredLsgds = useMemo(() =>
    fDistrict ? hier.lsgds.filter(x => x.parent_id === fDistrict) : hier.lsgds,
    [fDistrict, hier.lsgds]);
  const filteredWards = useMemo(() =>
    fLsgd ? hier.wards.filter(x => x.parent_id === fLsgd) : hier.wards,
    [fLsgd, hier.wards]);

  const applyScope = () => {
    if (fWard) { setLevel("ward"); setScopeId(fWard); }
    else if (fLsgd) { setLevel("lsgd"); setScopeId(fLsgd); }
    else if (fDistrict) { setLevel("district"); setScopeId(fDistrict); }
    else if (fState) { setLevel("state"); setScopeId(fState); }
    else { setScopeId(""); }
  };
  const clearScope = () => {
    setFState(""); setFDistrict(""); setFLsgd(""); setFWard("");
    setScopeId("");
  };

  const activeScopeLabel = () => {
    if (fWard) return hier.wards.find(x => x.id === fWard)?.name;
    if (fLsgd) return hier.lsgds.find(x => x.id === fLsgd)?.name;
    if (fDistrict) return hier.districts.find(x => x.id === fDistrict)?.name;
    if (fState) return hier.states.find(x => x.id === fState)?.name;
    return null;
  };

  const downloadPdf = async () => {
    try {
      const q = `?level=${level}${scopeId ? `&scope_id=${scopeId}` : ""}`;
      const r = await api.get(`/gov/dashboard/report.pdf${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `regulatory-dashboard-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (load) return <Loader/>;

  return (
    <div className="space-y-6" data-testid="gov-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <Header
          eyebrow="Jurisdiction Intelligence"
          title="Regulatory Dashboard"
          sub={activeScopeLabel() ? `Filtered view: ${activeScopeLabel()}` : "Real-time compliance state of your jurisdiction"}/>
        <button onClick={downloadPdf} data-testid="dashboard-pdf"
           className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: BLUE }}>
          <FileText size={13}/> Download PDF Report
        </button>
      </div>

      {/* Hierarchy filter bar */}
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-4" data-testid="hier-filter">
        <div className="flex flex-wrap items-end gap-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55] pb-2.5">Hierarchy filter</div>
          <HierSelect label="State" value={fState}
                       options={hier.states} onChange={(v) => { setFState(v); setFDistrict(""); setFLsgd(""); setFWard(""); }}
                       testid="hier-state"/>
          <HierSelect label="District" value={fDistrict}
                       options={filteredDistricts} disabled={hier.states.length > 0 && !fState}
                       onChange={(v) => { setFDistrict(v); setFLsgd(""); setFWard(""); }}
                       testid="hier-district"/>
          <HierSelect label="LSGD" value={fLsgd}
                       options={filteredLsgds} disabled={!fDistrict}
                       onChange={(v) => { setFLsgd(v); setFWard(""); }}
                       testid="hier-lsgd"/>
          <HierSelect label="Ward" value={fWard}
                       options={filteredWards} disabled={!fLsgd}
                       onChange={setFWard} testid="hier-ward"/>
          <div className="flex items-center gap-2 pb-0.5">
            <button onClick={applyScope} data-testid="hier-apply"
                    className="h-9 px-4 rounded-md text-white text-xs font-medium" style={{ background: BLUE }}>
              Apply
            </button>
            <button onClick={clearScope} data-testid="hier-clear"
                    className="h-9 px-3 rounded-md text-xs border border-[#0B2B66]/20 text-[#5C5A55] hover:bg-[#0B2B66]/5">
              Reset
            </button>
          </div>
        </div>
      </section>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Generators" v={d?.total_generators} Icon={User} accent={BLUE}/>
        <KpiCard label="Pending Verification" v={d?.pending_generators} Icon={Warning} accent={RED}/>
        <KpiCard label="Compliance Score"
                 v={`${d?.compliance_score_pct ?? 0}%`}
                 Icon={ShieldCheck} accent={GREEN}/>
        <KpiCard label="Open Complaints" v={d?.complaints_open} Icon={XCircle} accent={RED}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Waste 30d (kg)"
                 v={Math.round(d?.waste_30d_kg || 0).toLocaleString()}
                 Icon={Leaf} accent={GREEN}/>
        <KpiCard label="CO2 Saved (kg)"
                 v={Math.round(d?.co2_30d_kg || 0).toLocaleString()}
                 Icon={Leaf} accent={GREEN}/>
        <KpiCard label="Missed Pickups 30d" v={d?.missed_pickups_30d} Icon={Warning} accent={RED}/>
        <KpiCard label="Disposal Certificates" v={d?.disposal_certificates} Icon={Certificate} accent={BLUE}/>
      </div>

      {/* Performance chart — monthly trend */}
      {sust && (
        <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6" data-testid="perf-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Performance Trend</div>
              <h2 className="font-heading text-xl" style={{ color: BLUE }}>12-month Waste Volume & CO2 Impact</h2>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={sust.months}>
                <CartesianGrid stroke="#eee" strokeDasharray="3 3"/>
                <XAxis dataKey="month" fontSize={10}/>
                <YAxis fontSize={10}/>
                <Tooltip/>
                <Bar dataKey="kg" fill={BLUE} name="Waste (kg)"/>
                <Bar dataKey="co2_kg" fill={GREEN} name="CO2 saved (kg)"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Performance ranking — who's doing best/worst */}
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6" data-testid="perf-ranking">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Comparative Performance</div>
            <h2 className="font-heading text-xl" style={{ color: BLUE }}>Ranking by {LEVELS.find(l => l.key === level)?.label}</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-[#0B2B66]/20 overflow-hidden" data-testid="perf-level">
              {LEVELS.map(l => (
                <button key={l.key} onClick={() => setLevel(l.key)}
                        className={`px-3 h-9 text-xs ${level === l.key ? "bg-[#0B2B66] text-white" : "bg-white text-[#5C5A55] hover:bg-[#0B2B66]/5"}`}
                        data-testid={`perf-level-${l.key}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <select value={perfSort} onChange={(e) => setPerfSort(e.target.value)} data-testid="perf-sort"
                    className="h-9 px-3 border border-[#0B2B66]/20 rounded-md text-xs bg-white">
              <option value="compliance_desc">Best compliance first</option>
              <option value="compliance_asc">Worst compliance first</option>
              <option value="generators_desc">Most generators</option>
              <option value="waste_desc">Highest waste volume</option>
            </select>
          </div>
        </div>

        {perf && (
          <>
            {/* Comparative bar chart */}
            {perf.rows.length > 0 ? (
              <div style={{ width: "100%", height: 240 }} className="mb-6">
                <ResponsiveContainer>
                  <BarChart data={perf.rows.slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid stroke="#eee" strokeDasharray="3 3"/>
                    <XAxis type="number" fontSize={10} domain={[0, 100]}/>
                    <YAxis type="category" dataKey="name" fontSize={10} width={110}/>
                    <Tooltip formatter={(v) => `${v}%`}/>
                    <Bar dataKey="compliance_pct" name="Compliance %">
                      {perf.rows.slice(0, 10).map((r, i) => (
                        <Cell key={i} fill={r.compliance_pct >= 75 ? GREEN :
                                               r.compliance_pct >= 50 ? GOLD : RED}/>
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-10 text-sm text-[#5C5A55] italic bg-[#FAFBFF] rounded-lg mb-4 border border-dashed border-[#0B2B66]/10">
                No regions to rank at this level yet — data will appear once generators register in this jurisdiction.
              </div>
            )}

            {/* Ranking table */}
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
                    <th className="p-2 text-left font-semibold w-10">#</th>
                    <th className="p-2 text-left font-semibold">{LEVELS.find(l => l.key === level)?.label}</th>
                    <th className="p-2 text-right font-semibold">Generators</th>
                    <th className="p-2 text-right font-semibold">Active</th>
                    <th className="p-2 text-left font-semibold w-56">Compliance</th>
                    <th className="p-2 text-right font-semibold">Waste 30d (kg)</th>
                    <th className="p-2 text-center font-semibold">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.rows.map((r, i) => (
                    <tr key={r.id} className="border-b border-[#0B2B66]/5 hover:bg-[#0B2B66]/2" data-testid={`perf-row-${r.id}`}>
                      <td className="p-2 font-mono text-xs text-[#5C5A55]">{i + 1}</td>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2 text-right">{r.total_generators}</td>
                      <td className="p-2 text-right">{r.active_generators}</td>
                      <td className="p-2">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 rounded-full bg-[#0B2B66]/10 overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${r.compliance_pct}%` }}
                              transition={{ duration: 0.8, delay: i * 0.03 }}
                              className="h-full rounded-full"
                              style={{ background: r.compliance_pct >= 75 ? GREEN : r.compliance_pct >= 50 ? GOLD : RED }}/>
                          </div>
                          <span className="text-xs font-mono font-semibold w-12 text-right">{r.compliance_pct}%</span>
                        </div>
                      </td>
                      <td className="p-2 text-right font-mono text-xs">{r.waste_kg_30d.toLocaleString()}</td>
                      <td className="p-2 text-center">
                        <RatingPill r={r.rating}/>
                      </td>
                    </tr>
                  ))}
                  {perf.rows.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-sm text-[#5C5A55] italic">
                      No data at this level for your jurisdiction.
                    </td></tr>
                  )}
                </tbody>
                {perf.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-[#0B2B66]/15 text-xs font-semibold" style={{ color: BLUE }}>
                      <td className="p-2" colSpan={2}>TOTAL · {perf.rows.length} {level}(s)</td>
                      <td className="p-2 text-right">{perf.totals.total_generators}</td>
                      <td className="p-2 text-right">{perf.totals.active_generators}</td>
                      <td className="p-2">Avg: <span className="font-mono">{perf.totals.avg_compliance_pct}%</span></td>
                      <td className="p-2 text-right font-mono">{perf.totals.waste_kg_30d.toLocaleString()}</td>
                      <td/>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </section>

      {/* BMW analytics mini */}
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>BMW 2016 Compliance</div>
            <h2 className="font-heading text-xl" style={{ color: BLUE }}>Waste by Regulatory Category</h2>
          </div>
          <Link to="/gov/bmw" className="text-xs text-[#0B2B66] hover:underline inline-flex items-center gap-1">
            Full report <ArrowRight size={11}/>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {bmw && Object.entries(bmw.buckets).map(([c, b]) => (
            <BmwCard key={c} color={c} data={b}/>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3 text-xs text-[#5C5A55]">
          <span>Segregation compliance:</span>
          <div className="flex-1 h-2 rounded-full bg-[#0B2B66]/10 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${bmw?.segregation_compliance_pct || 0}%` }}
              transition={{ duration: 1.2 }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${RED}, ${GOLD}, ${GREEN})` }}/>
          </div>
          <span className="font-mono font-semibold" style={{ color: BLUE }}>
            {bmw?.segregation_compliance_pct ?? 0}%
          </span>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// BMW Reports
// ============================================================================
export function GovBMWReports() {
  const [period, setPeriod] = useState("monthly");
  const [d, setD] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get(`/gov/bmw-analytics?period=${period}`); setD(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, [period]);

  const pieData = d ? Object.entries(d.buckets).map(([k, b]) => ({
    name: b.label, value: b.bags, fill: b.hex,
  })).filter(x => x.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="gov-bmw">
      <Header eyebrow="BMW Rules 2016" title="Biomedical Waste Analytics"
              sub="Category-coded compliance breakdown · Yellow · Red · White · Blue"/>
      <PeriodChips period={period} onChange={setPeriod}/>
      <div className="grid md:grid-cols-2 gap-4">
        {d && Object.entries(d.buckets).map(([c, b]) => (
          <BmwCardLarge key={c} color={c} data={b}/>
        ))}
      </div>
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6">
        <h3 className="font-heading text-lg mb-4" style={{ color: BLUE }}>Volume distribution</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100}
                   label={(e) => `${e.name} (${e.value})`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill}/>)}
              </Pie>
              <Tooltip/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// Compliance Monitoring
// ============================================================================
export function GovCompliance() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => {
    try { const r = await api.get("/gov/compliance"); setRows(r.data.rows); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="gov-compliance">
      <Header eyebrow="Regional Oversight" title="Compliance Monitoring"
              sub="Per-ward compliance roll-up — sorted worst to best to flag at-risk regions"/>
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="p-3 text-left font-semibold">Ward / LSGD</th>
              <th className="p-3 text-left font-semibold">Total Generators</th>
              <th className="p-3 text-left font-semibold">Active</th>
              <th className="p-3 text-left font-semibold">Compliance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[#0B2B66]/5">
                <td className="p-3 font-mono text-[11px]">{(r.ward_id || r.lsgd_id || "—").slice(0, 10)}…</td>
                <td className="p-3">{r.total_generators}</td>
                <td className="p-3">{r.active}</td>
                <td className="p-3 w-64">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 rounded-full bg-[#0B2B66]/10 overflow-hidden">
                      <div className="h-full rounded-full"
                           style={{ width: `${r.compliance_pct}%`,
                                     background: r.compliance_pct > 80 ? GREEN : r.compliance_pct > 50 ? GOLD : RED }}/>
                    </div>
                    <span className="text-xs font-mono font-semibold w-12 text-right">{r.compliance_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-sm text-[#5C5A55]">No compliance data for this jurisdiction.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ============================================================================
// Audit Trail (chain of custody)
// ============================================================================
export function GovAuditTrail() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => {
    try { const r = await api.get("/gov/audit-trail?limit=200"); setRows(r.data.rows); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="gov-audit">
      <Header eyebrow="Legal Evidence" title="Chain-of-Custody Audit Trail"
              sub="Every QR bag from generation → collection → disposal · tamper-evident · court-admissible"/>
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="p-3 text-left font-semibold">QR Code</th>
              <th className="p-3 text-left font-semibold">Generator</th>
              <th className="p-3 text-left font-semibold">Staff</th>
              <th className="p-3 text-left font-semibold">Collected</th>
              <th className="p-3 text-left font-semibold">Weight</th>
              <th className="p-3 text-left font-semibold">GPS</th>
              <th className="p-3 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#0B2B66]/5 text-xs">
                <td className="p-3 font-mono">{r.qr_code}</td>
                <td className="p-3">{r.customer_name || r.customer_id?.slice(0, 8)}</td>
                <td className="p-3">{r.collected_by_staff_name || "—"}</td>
                <td className="p-3 text-[11px]">{(r.collected_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3">{r.collected_weight_kg ? `${r.collected_weight_kg} kg` : "—"}</td>
                <td className="p-3 text-[10px] font-mono">
                  {r.collection_gps ? `${r.collection_gps.latitude?.toFixed(3)}, ${r.collection_gps.longitude?.toFixed(3)}` : "—"}
                </td>
                <td className="p-3">
                  <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold bg-[#D4E0D1] text-[#1A3317]">
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-sm text-[#5C5A55]">No chain-of-custody records in this jurisdiction yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ============================================================================
// Certificate Verification
// ============================================================================
export function GovCertificateVerification() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.get(`/gov/certificates/verify/${encodeURIComponent(query.trim())}`);
      setResult(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="gov-cert-verify">
      <Header eyebrow="Regulatory Verification" title="Certificate Authenticity"
              sub="Search any certificate number to verify authenticity across Training · Customer Compliance · Disposal"/>
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6 max-w-2xl">
        <form onSubmit={verify} className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A55]"/>
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="cert-search"
                   placeholder="e.g. RSG-CERT-2026-000123"
                   className="w-full h-12 pl-9 pr-3 border border-[#0B2B66]/20 rounded-md text-sm font-mono outline-none focus:border-[#0B2B66]"/>
          </div>
          <button type="submit" disabled={busy} data-testid="cert-verify-btn"
                  className="h-12 px-6 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" style={{ background: BLUE }}>
            {busy ? "Verifying…" : "Verify"} <ArrowRight size={12} weight="bold"/>
          </button>
        </form>
        {result && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-5 p-5 rounded-lg border"
                      style={{ borderColor: result.valid ? GREEN : RED,
                                background: result.valid ? "#E8EFE6" : "#FBE7DD" }}
                      data-testid="cert-result">
            {result.valid ? (
              <>
                <div className="inline-flex items-center gap-2 font-heading text-lg" style={{ color: GREEN }}>
                  <CheckCircle size={18} weight="fill"/> Authentic Certificate
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <KV k="Certificate No" v={result.certificate_no} mono/>
                  <KV k="Type" v={result.cert_type}/>
                  <KV k="Recipient" v={result.recipient_name}/>
                  <KV k="Course / Subject" v={result.course_name || "—"}/>
                  <KV k="Issued" v={(result.issued_at || "").slice(0, 10)}/>
                  <KV k="Valid until" v={result.valid_until}/>
                </div>
                {result.expired && (
                  <div className="mt-3 text-xs px-3 py-2 rounded bg-[#F9E4B7] text-[#7A5A11]">
                    ⚠ This certificate has expired.
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2" style={{ color: RED }}>
                <XCircle size={18} weight="fill"/> <b>{result.reason || "Invalid certificate"}</b>
                {result.revoked && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] font-semibold">REVOKED</span>}
              </div>
            )}
          </motion.div>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// Sustainability Metrics
// ============================================================================
export function GovSustainability() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await api.get("/gov/sustainability"); setD(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);

  return (
    <div className="space-y-6" data-testid="gov-sustainability">
      <Header eyebrow="Environmental Impact" title="Sustainability Metrics"
              sub="12-month environmental performance of your jurisdiction"/>
      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard label="Total waste processed (kg)"
                     v={Math.round(d.totals.kg).toLocaleString()}
                     Icon={Leaf} accent={GREEN}/>
            <KpiCard label="CO2 equivalent saved (kg)"
                     v={Math.round(d.totals.co2_kg).toLocaleString()}
                     Icon={Leaf} accent={GREEN}/>
            <KpiCard label="Total bags tracked"
                     v={d.totals.bags.toLocaleString()}
                     Icon={Certificate} accent={BLUE}/>
          </div>
          <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6">
            <h3 className="font-heading text-lg mb-4" style={{ color: BLUE }}>Monthly Trend (kg)</h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={d.months}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3"/>
                  <XAxis dataKey="month" fontSize={10}/>
                  <YAxis fontSize={10}/>
                  <Tooltip/>
                  <Line type="monotone" dataKey="kg" stroke={BLUE} strokeWidth={2} dot={{ r: 3 }}/>
                  <Line type="monotone" dataKey="co2_kg" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// ============================================================================
// User Profile
// ============================================================================
export function GovProfile() {
  const [me, setMe] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await api.get("/gov/me"); setMe(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);
  if (!me) return <Loader/>;
  return (
    <div className="space-y-6" data-testid="gov-profile">
      <Header eyebrow="Officer Account" title={me.name || me.email}
              sub={me.gov_role ? me.gov_role.replace(/_/g, " ").toUpperCase() : "—"}/>
      <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6">
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <KV k="Email" v={me.email} mono/>
          <KV k="Phone" v={me.phone || "—"}/>
          <KV k="Department" v={me.department || "—"}/>
          <KV k="Designation" v={me.designation || "—"}/>
          <KV k="Government ID" v={me.government_id || "—"} mono/>
          <KV k="Hierarchy Role" v={(me.gov_role || "—").replace(/_/g, " ").toUpperCase()}/>
        </div>
        <div className="mt-6 pt-6 border-t border-[#0B2B66]/10">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55] mb-3">Jurisdiction Scope</div>
          <div className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
               style={{ background: "#EAF1FD", color: BLUE }}>
            <ShieldCheck size={11} weight="fill" className="inline mr-1"/>
            {me.jurisdiction?.scope || "unknown"} access
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <ScopeChip label="States" n={me.jurisdiction?.states?.length || 0}/>
            <ScopeChip label="Districts" n={me.jurisdiction?.districts?.length || 0}/>
            <ScopeChip label="LSGDs" n={me.jurisdiction?.lsgds?.length || 0}/>
            <ScopeChip label="Wards" n={me.jurisdiction?.wards?.length || 0}/>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// Shared primitives
// ============================================================================
const Header = ({ eyebrow, title, sub }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: RED }}>{eyebrow}</div>
    <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>{title}</h1>
    {sub && <p className="text-sm text-[#5C5A55] mt-1">{sub}</p>}
  </div>
);

const KpiCard = ({ label, v, Icon, accent }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border border-[#0B2B66]/10 p-4 relative overflow-hidden">
    <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full opacity-5"
         style={{ background: accent || BLUE }}/>
    <Icon size={18} weight="duotone" color={accent || BLUE}/>
    <div className="font-heading text-3xl mt-2" style={{ color: accent || BLUE }}>{v ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{label}</div>
  </motion.div>
);

const BMW_ICON = { yellow: Flask, red: TestTube, white: Syringe, blue: Drop };

const BmwCard = ({ color, data }) => {
  const Icon = BMW_ICON[color] || TrashIcon;
  return (
    <div className="relative rounded-lg p-4 border overflow-hidden"
         style={{ borderColor: data.hex, background: `${data.hex}15` }}
         data-testid={`bmw-${color}`}>
      <div className="flex items-start justify-between">
        <Icon size={20} color={data.hex} weight="duotone"/>
        <span className="text-[10px] font-mono" style={{ color: data.hex }}>{data.share_pct}%</span>
      </div>
      <div className="font-heading text-xl mt-2" style={{ color: data.hex === "#E0E0E0" ? "#333" : data.hex }}>{data.bags.toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{data.label}</div>
      <div className="text-[10px] text-[#5C5A55] mt-1">{data.kg} kg</div>
    </div>
  );
};

const BmwCardLarge = ({ color, data }) => {
  const Icon = BMW_ICON[color] || TrashIcon;
  return (
    <div className="relative rounded-xl p-6 border overflow-hidden"
         style={{ borderColor: data.hex, background: `${data.hex}10` }}
         data-testid={`bmw-large-${color}`}>
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-lg grid place-items-center"
             style={{ background: data.hex, color: data.hex === "#E0E0E0" ? "#333" : "#fff" }}>
          <Icon size={22} weight="fill"/>
        </div>
        <div className="text-right">
          <div className="font-heading text-3xl" style={{ color: data.hex === "#E0E0E0" ? "#333" : data.hex }}>{data.bags.toLocaleString()}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">bags</div>
        </div>
      </div>
      <h3 className="font-heading text-lg mt-3" style={{ color: BLUE }}>{data.label}</h3>
      <p className="text-[11px] text-[#5C5A55] mt-1 leading-relaxed">{data.contents}</p>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <div><b>{data.kg}</b> kg</div>
        <div className="text-[#5C5A55]">·</div>
        <div><b>{data.share_pct}%</b> of total</div>
      </div>
    </div>
  );
};

const PeriodChips = ({ period, onChange }) => (
  <div className="flex flex-wrap items-center gap-2">
    {[["daily", "Today"], ["weekly", "7 days"], ["monthly", "Month"], ["yearly", "Year"]].map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)} data-testid={`period-${v}`}
              className={`h-8 px-3 rounded-md text-xs border ${period === v ? "bg-[#0B2B66] text-white border-[#0B2B66]" : "border-[#0B2B66]/20 text-[#5C5A55] hover:bg-[#0B2B66]/5"}`}>
        {l}
      </button>
    ))}
    <div className="flex-1"/>
    <a href={`${API}/gov/waste-report?period=${period}&format=csv`}
       className="h-8 px-3 rounded-md text-xs border border-[#0B2B66]/20 text-[#0B2B66] hover:bg-[#0B2B66]/5 inline-flex items-center gap-1"
       target="_blank" rel="noreferrer" data-testid="export-csv">
      <DownloadSimple size={11}/> CSV
    </a>
    <a href={`${API}/gov/waste-report?period=${period}&format=pdf`}
       className="h-8 px-3 rounded-md text-xs border border-[#0B2B66]/20 text-[#0B2B66] hover:bg-[#0B2B66]/5 inline-flex items-center gap-1"
       target="_blank" rel="noreferrer" data-testid="export-pdf">
      <FileText size={11}/> PDF
    </a>
  </div>
);

const KV = ({ k, v, mono }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#5C5A55]">{k}</div>
    <div className={mono ? "font-mono text-xs mt-0.5" : "text-sm mt-0.5 font-medium"}>{v}</div>
  </div>
);

const ScopeChip = ({ label, n }) => (
  <div className={`rounded-md border p-3 ${n > 0 ? "border-[#0B2B66]/20 bg-[#EAF1FD]" : "border-[#0B2B66]/10"}`}>
    <div className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
    <div className="font-heading text-lg mt-0.5" style={{ color: n > 0 ? BLUE : "#8A8780" }}>{n || "All"}</div>
  </div>
);

const Loader = () => (
  <div className="p-12 text-center text-[#5C5A55]">
    <Gauge size={20} className="inline animate-pulse mr-2"/>Loading…
  </div>
);

const HierSelect = ({ label, value, options, onChange, disabled, testid }) => (
  <div className={disabled ? "opacity-40" : ""}>
    <label className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55] block mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
            data-testid={testid}
            className="h-9 px-3 border border-[#0B2B66]/20 rounded-md text-xs bg-white min-w-[130px] outline-none focus:border-[#0B2B66]">
      <option value="">All {label.toLowerCase()}s</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  </div>
);

const RATING = {
  excellent: { bg: "#D4E0D1", color: "#1A3317", label: "Excellent" },
  good:      { bg: "#EAF1FD", color: "#11427A", label: "Good" },
  moderate:  { bg: "#F9E4B7", color: "#7A5A11", label: "Moderate" },
  at_risk:   { bg: "#F4C7B9", color: "#7A2E18", label: "At risk" },
};
const RatingPill = ({ r }) => {
  const s = RATING[r] || RATING.moderate;
  return <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold"
                 style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};
